<template>
  <div class="robot-activation">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/menu/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>用户管理</el-breadcrumb-item>
      <el-breadcrumb-item>机器人激活管理</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card>
      <div class="toolbar">
        <el-input
          v-model.trim="deviceCode"
          clearable
          placeholder="输入管理名称、Temi 名称、编码或序列号"
          class="device-search"
          @keyup.enter.native="search"
        />
        <el-button type="primary" :loading="loading" @click="search">查询</el-button>
        <el-button :loading="loading" @click="loadList">刷新</el-button>
      </div>

      <el-alert
        title="设备首次启动查询时会自动创建记录。到期时间为空或已过期时，机器人端将停留在等待激活页面。"
        type="info"
        :closable="false"
        class="notice"
      />

      <el-table v-loading="loading" :data="rows" border stripe>
        <el-table-column prop="deviceName" label="管理名称" min-width="160" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.deviceName || '未命名' }}</template>
        </el-table-column>
        <el-table-column label="Temi 机器人名称" min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.temiRobotName || '暂未上报' }}</template>
        </el-table-column>
        <el-table-column prop="deviceCode" label="设备编码" min-width="230" />
        <el-table-column label="Temi 序列号" min-width="180">
          <template slot-scope="scope">{{ scope.row.temiSerialNumber || '暂未上报' }}</template>
        </el-table-column>
        <el-table-column prop="createTime" label="首次查询时间" width="180" />
        <el-table-column prop="expireTime" label="到期时间" width="180">
          <template slot-scope="scope">{{ scope.row.expireTime || '未激活' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.active ? 'success' : 'info'">
              {{ scope.row.active ? '已激活' : '未激活' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="设置密码" width="160">
          <template slot-scope="scope">
            <el-tag :type="scope.row.settingsPasswordConfigured ? 'success' : 'warning'">
              {{ scope.row.settingsPasswordConfigured ? '已配置' : '未配置' }}
            </el-tag>
            <el-button
              v-if="scope.row.settingsPasswordConfigured"
              type="text"
              size="mini"
              icon="el-icon-view"
              @click="viewPassword(scope.row)"
            >查看</el-button>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="220" show-overflow-tooltip />
        <el-table-column prop="updateTime" label="更新时间" width="180" />
        <el-table-column label="操作" width="420" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" @click="openMetadata(scope.row)">编辑资料</el-button>
            <el-button size="mini" type="primary" @click="openEdit(scope.row)">设置到期时间</el-button>
            <el-button size="mini" @click="openPassword(scope.row)">
              {{ scope.row.settingsPasswordConfigured ? '更新密码' : '设置密码' }}
            </el-button>
            <el-button
              size="mini"
              type="danger"
              :disabled="!scope.row.expireTime"
              @click="clearActivation(scope.row)"
            >取消激活</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next"
        @current-change="changePage"
      />
    </el-card>

    <el-dialog title="设置机器人激活到期时间" :visible.sync="dialogVisible" width="460px">
      <el-form label-width="110px">
        <el-form-item label="设备编码">{{ editingRow && editingRow.deviceCode }}</el-form-item>
        <el-form-item label="Temi 序列号">{{ (editingRow && editingRow.temiSerialNumber) || '暂未上报' }}</el-form-item>
        <el-form-item label="到期时间">
          <el-date-picker
            v-model="expireTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss"
            placeholder="选择到期时间"
            :picker-options="pickerOptions"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveActivation">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="编辑设备资料"
      :visible.sync="metadataVisible"
      width="560px"
      :close-on-click-modal="false"
      :before-close="closeMetadata"
    >
      <el-form label-width="100px" @submit.native.prevent="saveMetadata">
        <el-form-item label="设备编码">{{ metadataRow && metadataRow.deviceCode }}</el-form-item>
        <el-form-item label="Temi 序列号">{{ (metadataRow && metadataRow.temiSerialNumber) || '暂未上报' }}</el-form-item>
        <el-form-item label="Temi 名称">{{ (metadataRow && metadataRow.temiRobotName) || '暂未上报' }}</el-form-item>
        <el-form-item label="管理名称">
          <el-input v-model="deviceName" maxlength="100" show-word-limit :disabled="metadataSaving" placeholder="输入激活管理使用的名称" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="remark" type="textarea" :rows="4" maxlength="500" show-word-limit :disabled="metadataSaving" placeholder="输入备注" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button :disabled="metadataSaving" @click="closeMetadata()">取消</el-button>
        <el-button type="primary" :loading="metadataSaving" @click="saveMetadata">保存资料</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="查看机器人设置密码"
      :visible.sync="passwordViewVisible"
      width="460px"
      :close-on-click-modal="false"
      @close="clearViewedPassword"
    >
      <el-form v-loading="passwordViewing" label-width="100px">
        <el-form-item label="管理名称">{{ (passwordViewRow && passwordViewRow.deviceName) || '未命名' }}</el-form-item>
        <el-form-item label="设备编码">{{ passwordViewRow && passwordViewRow.deviceCode }}</el-form-item>
        <el-form-item label="当前密码">
          <el-input v-if="viewedPassword" :value="viewedPassword" readonly class="viewed-password" />
          <span v-else>{{ passwordViewError || '正在读取' }}</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="passwordViewVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="passwordRow && passwordRow.settingsPasswordConfigured ? '更新机器人设置密码' : '配置机器人设置密码'"
      :visible.sync="passwordVisible"
      width="480px"
      :close-on-click-modal="false"
      :before-close="closePassword"
      @closed="clearPassword"
    >
      <el-form label-width="110px" @submit.native.prevent="savePassword">
        <el-form-item label="设备编码">{{ passwordRow && passwordRow.deviceCode }}</el-form-item>
        <el-form-item label="Temi 序列号">{{ (passwordRow && passwordRow.temiSerialNumber) || '暂未上报' }}</el-form-item>
        <el-form-item label="设置密码">
          <el-input
            v-model="settingsPassword"
            type="password"
            show-password
            maxlength="4"
            autocomplete="new-password"
            placeholder="输入4位数字，支持以0开头"
            :disabled="passwordSaving || passwordGenerating"
          />
          <el-button
            class="generate-password"
            size="small"
            icon="el-icon-refresh"
            :loading="passwordGenerating"
            :disabled="passwordSaving"
            @click="generatePassword"
          >自动生成4位密码</el-button>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button :disabled="passwordSaving" @click="closePassword()">取消</el-button>
        <el-button
          type="primary"
          :loading="passwordSaving"
          :disabled="passwordGenerating"
          @click="savePassword"
        >保存密码</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { DatePicker, Alert } from 'element-ui'

export default {
  name: 'RobotActivation',
  components: {
    ElDatePicker: DatePicker,
    ElAlert: Alert
  },
  data () {
    return {
      deviceCode: '',
      rows: [],
      page: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      saving: false,
      dialogVisible: false,
      editingRow: null,
      expireTime: '',
      passwordRow: null,
      passwordVisible: false,
      settingsPassword: '',
      passwordSaving: false,
      passwordGenerating: false,
      passwordViewVisible: false,
      passwordViewRow: null,
      passwordViewing: false,
      viewedPassword: '',
      passwordViewError: '',
      passwordViewRequest: 0,
      metadataVisible: false,
      metadataRow: null,
      deviceName: '',
      remark: '',
      metadataSaving: false,
      pickerOptions: {
        disabledDate (date) {
          return date.getTime() < Date.now() - 24 * 60 * 60 * 1000
        }
      }
    }
  },
  created () {
    this.loadList()
  },
  beforeDestroy () {
    this.clearViewedPassword()
  },
  methods: {
    openMetadata (row) {
      this.metadataRow = row
      this.deviceName = row.deviceName || ''
      this.remark = row.remark || ''
      this.metadataVisible = true
    },
    closeMetadata (done) {
      if (this.metadataSaving) return
      if (typeof done === 'function') done()
      else this.metadataVisible = false
    },
    async saveMetadata () {
      if (this.metadataSaving || !this.metadataRow) return
      if (this.deviceName.trim().length > 100 || this.remark.trim().length > 500) {
        this.$message.warning('管理名称限100字，备注限500字')
        return
      }
      this.metadataSaving = true
      try {
        const { data: resp } = await this.$http.put(`cms/robot-activation/${this.metadataRow.id}/metadata`, {
          deviceName: this.deviceName.trim(),
          remark: this.remark.trim()
        })
        if (resp.code !== 200) throw new Error(resp.message || '设备资料保存失败')
        this.$message.success('设备资料已保存')
        this.metadataVisible = false
        this.loadList()
      } catch (error) {
        this.$message.error(error.message || '设备资料保存失败')
      } finally {
        this.metadataSaving = false
      }
    },
    clearViewedPassword () {
      this.passwordViewRequest++
      this.viewedPassword = ''
      this.passwordViewError = ''
      this.passwordViewRow = null
      this.passwordViewing = false
    },
    async viewPassword (row) {
      this.clearViewedPassword()
      const request = this.passwordViewRequest
      this.passwordViewRow = row
      this.passwordViewVisible = true
      this.passwordViewing = true
      try {
        const { data: resp } = await this.$http.get(`cms/robot-activation/${row.id}/settings-password`)
        if (request !== this.passwordViewRequest || !this.passwordViewVisible) return
        if (resp.code !== 200) throw new Error(resp.message || '密码读取失败')
        this.viewedPassword = resp.data.password
      } catch (error) {
        if (request === this.passwordViewRequest && this.passwordViewVisible) {
          this.passwordViewError = error.message || '密码读取失败'
        }
      } finally {
        if (request === this.passwordViewRequest) this.passwordViewing = false
      }
    },
    openPassword (row) {
      this.passwordRow = row
      this.settingsPassword = ''
      this.passwordVisible = true
    },
    closePassword (done) {
      if (this.passwordSaving) return
      if (typeof done === 'function') done()
      else this.passwordVisible = false
    },
    clearPassword () {
      this.settingsPassword = ''
      this.passwordRow = null
    },
    async generatePassword () {
      if (this.passwordGenerating || this.passwordSaving) return
      this.passwordGenerating = true
      const row = this.passwordRow
      try {
        const { data: resp } = await this.$http.post('cms/robot-activation/settings-password/generate')
        if (resp.code !== 200) throw new Error(resp.message || '密码生成失败')
        if (this.passwordVisible && this.passwordRow === row) this.settingsPassword = resp.data.password
      } catch (error) {
        this.$message.error(error.message || '密码生成失败')
      } finally {
        this.passwordGenerating = false
      }
    },
    async savePassword () {
      if (this.passwordSaving || this.passwordGenerating || !this.passwordRow) return
      if (!/^[0-9]{4}$/.test(this.settingsPassword)) {
        this.$message.warning('请输入4位数字密码')
        return
      }
      this.passwordSaving = true
      try {
        const { data: resp } = await this.$http.put(
          `cms/robot-activation/${this.passwordRow.id}/settings-password`,
          { password: this.settingsPassword }
        )
        if (resp.code !== 200) throw new Error(resp.message || '设置密码保存失败')
        this.$message.success('设置密码已保存，请使用新密码进入机器人设置')
        this.settingsPassword = ''
        this.passwordVisible = false
        this.loadList()
      } catch (error) {
        this.$message.error(error.message || '设置密码保存失败')
      } finally {
        this.passwordSaving = false
      }
    },
    search () {
      this.page = 1
      this.loadList()
    },
    changePage (page) {
      this.page = page
      this.loadList()
    },
    async loadList () {
      this.loading = true
      try {
        const { data: resp } = await this.$http.get('cms/robot-activation', {
          params: {
            deviceCode: this.deviceCode || undefined,
            page: this.page,
            size: this.pageSize
          }
        })
        if (resp.code !== 200) throw new Error(resp.message || '激活记录读取失败')
        this.rows = resp.data.rows || []
        this.total = resp.data.total || 0
      } catch (error) {
        this.$message.error(error.message || '激活记录读取失败')
      } finally {
        this.loading = false
      }
    },
    openEdit (row) {
      this.editingRow = row
      this.expireTime = row.expireTime || ''
      this.dialogVisible = true
    },
    async saveActivation () {
      if (!this.editingRow || !this.expireTime) {
        this.$message.warning('请选择到期时间')
        return
      }
      this.saving = true
      try {
        const { data: resp } = await this.$http.put(`cms/robot-activation/${this.editingRow.id}`, {
          expireTime: this.expireTime
        })
        if (resp.code !== 200) throw new Error(resp.message || '激活保存失败')
        this.$message.success('激活到期时间已保存')
        this.dialogVisible = false
        this.loadList()
      } catch (error) {
        this.$message.error(error.message || '激活保存失败')
      } finally {
        this.saving = false
      }
    },
    clearActivation (row) {
      this.$confirm(`确定取消设备 ${row.deviceCode} 的激活吗？`, '取消激活', {
        type: 'warning',
        confirmButtonText: '确定取消',
        cancelButtonText: '返回'
      }).then(async () => {
        this.saving = true
        try {
          const { data: resp } = await this.$http.put(`cms/robot-activation/${row.id}`, { expireTime: null })
          if (resp.code !== 200) throw new Error(resp.message || '取消激活失败')
          this.$message.success('设备已取消激活')
          this.loadList()
        } catch (error) {
          this.$message.error(error.message || '取消激活失败')
        } finally {
          this.saving = false
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.robot-activation { padding: 20px; }
.robot-activation .el-card { margin-top: 20px; }
.toolbar { display: flex; gap: 12px; margin-bottom: 16px; }
.device-search { width: 380px; }
.notice { margin-bottom: 16px; }
.generate-password { margin-top: 10px; }
.viewed-password >>> input { font-family: monospace; font-size: 22px; }
.el-pagination { margin-top: 16px; text-align: right; }
</style>
